import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ lines: [], totalAmount: '0', subtotalAmount: '0', currency: 'GBP' });
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('luxurystyle_cart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        if (parsed && parsed.lines) {
          setCart(parsed);
        }
      } catch (e) {
        localStorage.removeItem('luxurystyle_cart');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cart && cart.lines && cart.lines.length > 0) {
      localStorage.setItem('luxurystyle_cart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('luxurystyle_cart');
    }
  }, [cart]);

  const recalculateTotal = (lines) => {
    const total = lines.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
    return total.toFixed(2);
  };

  const addItem = useCallback((variantId, quantity = 1, productInfo = {}) => {
    setLoading(true);
    
    try {
      setCart(prevCart => {
        const existingLines = prevCart?.lines || [];
        
        // Check if item already exists in cart
        const existingIndex = existingLines.findIndex(line => line.variantId === variantId);
        
        let newLines;
        if (existingIndex >= 0) {
          // Update quantity of existing item
          newLines = existingLines.map((line, index) => 
            index === existingIndex 
              ? { ...line, quantity: line.quantity + quantity }
              : line
          );
        } else {
          // Add new item
          const newItem = {
            id: `line_${Date.now()}`,
            variantId: variantId,
            quantity: quantity,
            productTitle: productInfo.title || 'Product',
            variantTitle: productInfo.variantTitle || '',
            price: productInfo.price || '0',
            currency: 'GBP',
            image: productInfo.image || null,
            productHandle: productInfo.handle || ''
          };
          newLines = [...existingLines, newItem];
        }
        
        const newTotal = recalculateTotal(newLines);
        
        return {
          ...prevCart,
          lines: newLines,
          totalAmount: newTotal,
          subtotalAmount: newTotal,
          currency: 'GBP'
        };
      });
      
      setCartOpen(true);
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const removeItem = useCallback((index) => {
    setCart(prevCart => {
      if (!prevCart || !prevCart.lines) return prevCart;
      
      const newLines = prevCart.lines.filter((_, i) => i !== index);
      const newTotal = recalculateTotal(newLines);
      
      return {
        ...prevCart,
        lines: newLines,
        totalAmount: newTotal,
        subtotalAmount: newTotal
      };
    });
  }, []);

  const updateQuantity = useCallback((index, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(index);
      return;
    }
    
    setCart(prevCart => {
      if (!prevCart || !prevCart.lines) return prevCart;
      
      const newLines = prevCart.lines.map((line, i) => 
        i === index ? { ...line, quantity: newQuantity } : line
      );
      const newTotal = recalculateTotal(newLines);
      
      return {
        ...prevCart,
        lines: newLines,
        totalAmount: newTotal,
        subtotalAmount: newTotal
      };
    });
  }, [removeItem]);

  const openCart = useCallback(() => setCartOpen(true), []);
  const closeCart = useCallback(() => setCartOpen(false), []);
  const toggleCart = useCallback(() => setCartOpen(prev => !prev), []);

  const cartCount = cart?.lines?.reduce((sum, line) => sum + line.quantity, 0) || 0;

  const checkout = useCallback(() => {
    // Build Shopify cart URL with items
    if (cart?.lines?.length > 0) {
      // Format: /cart/variant_id:quantity,variant_id:quantity
      const cartItems = cart.lines.map(line => {
        // Extract numeric variant ID from the global ID format
        const variantId = line.variantId.includes('/') 
          ? line.variantId.split('/').pop() 
          : line.variantId;
        return `${variantId}:${line.quantity}`;
      }).join(',');
      
      window.location.href = `https://luxurystyle.info/cart/${cartItems}`;
    }
  }, [cart]);

  const clearCart = useCallback(() => {
    localStorage.removeItem('luxurystyle_cart');
    setCart({ lines: [], totalAmount: '0', subtotalAmount: '0', currency: 'GBP' });
    setCartOpen(false);
  }, []);

  const value = {
    cart,
    cartOpen,
    loading,
    cartCount,
    addItem,
    removeItem,
    updateQuantity,
    openCart,
    closeCart,
    toggleCart,
    checkout,
    clearCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
